import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Flex, Result, Spin } from 'antd';
import { TranslationLanguage } from '@lingoping/core';
import { DeckSwipe, SwipeSummary } from './DeckSwipe.js';
import { DeckWordFace } from './DeckWordFace.js';
import { Echo } from './Echo.js';
import { CloudDeckWord, getCloudDecksApi } from '../services/cloudDecks.js';
import {
  DeckDecision,
  DecisionResult,
  applyDeckDecision,
  deckLearnQueue,
  undoDeckDecision,
} from '../services/deckDecisions.js';
import { classifyDeck, loadLearnerState } from '../services/deckStatus.js';
import { getBaseTranslationLanguage } from '../services/translationSettings.js';
import { deckWordPalette } from '../utils/deckWords.js';
import { platform } from '../platform.js';

interface Props {
  deckId: string;
  userId: string;
  exitLabel?: string;
  onExit: () => void;
  /** When set, the learner can leave before the last word with a button of this name. */
  stopLabel?: string;
}

/** One pass over the words of a deck the learner has not decided on yet, in the deck's order. */
export const DeckSession: React.FC<Props> = ({
  deckId,
  userId,
  exitLabel = 'Done',
  onExit,
  stopLabel,
}) => {
  const [title, setTitle] = useState('');
  const [words, setWords] = useState<CloudDeckWord[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [summary, setSummary] = useState<SwipeSummary | null>(null);
  const [remaining, setRemaining] = useState(0);
  const [round, setRound] = useState(0);
  const [language, setLanguage] = useState<TranslationLanguage>('en');
  const [finishing, setFinishing] = useState(false);
  const loadId = useRef(0);

  const load = useCallback(async () => {
    const id = ++loadId.current;
    setWords(null);
    setSummary(null);
    setFailed(false);
    const api = await getCloudDecksApi();
    // The saved copy opens at once; only a deck never opened before has to be fetched.
    let open = await api.readDeck(deckId);
    if (!open || (open.words.length === 0 && open.deck.word_count > 0)) {
      open = await api.refreshDeck(deckId, { force: true });
    }
    if (id !== loadId.current) return;
    if (!open || open.words.length === 0) {
      setFailed(true);
      return;
    }
    const [learner, lists, translationLanguage] = await Promise.all([
      loadLearnerState(),
      api.readLists(),
      getBaseTranslationLanguage(),
    ]);
    if (id !== loadId.current) return;
    const { undecided } = classifyDeck(open.words, learner, lists.deferrals);
    setTitle(open.deck.title);
    setLanguage(translationLanguage);
    // classifyDeckWords hands back the very objects it was given.
    setWords(undecided as CloudDeckWord[]);
    setRound((r) => r + 1);
  }, [deckId]);

  useEffect(() => {
    void load();
    return () => {
      loadId.current++;
    };
  }, [load]);

  const handleDecision = useCallback(
    (word: CloudDeckWord, decision: DeckDecision) =>
      applyDeckDecision(word, decision, { userId, deckId }),
    [userId, deckId]
  );

  const handleUndo = useCallback(
    (word: CloudDeckWord, decision: DeckDecision, result: DecisionResult) =>
      undoDeckDecision(word, decision, result),
    []
  );

  const handleDone = useCallback(
    async (s: SwipeSummary) => {
      // Chosen words are turned into cards in the background: wait for them, so the count of
      // words left is right.
      setFinishing(true);
      await deckLearnQueue.idle();
      const api = await getCloudDecksApi();
      const [open, learner, lists] = await Promise.all([
        api.readDeck(deckId),
        loadLearnerState(),
        api.readLists(),
      ]);
      setRemaining(open ? classifyDeck(open.words, learner, lists.deferrals).undecided.length : 0);
      setFinishing(false);
      setSummary(s);
      platform.notifyBackground({ type: 'REFRESH_BADGE' });
    },
    [deckId]
  );

  if (failed) {
    return (
      <Result
        icon={<Echo mood="sad" size={120} style={{ margin: '0 auto' }} />}
        title="This deck's words aren't available right now"
        subTitle="Check your internet connection and try again."
        extra={[
          <Button key="retry" type="primary" onClick={() => void load()}>
            Try again
          </Button>,
          <Button key="exit" onClick={onExit}>
            {exitLabel}
          </Button>,
        ]}
      />
    );
  }

  if (words === null || finishing) {
    return (
      <Flex justify="center" style={{ padding: 60 }}>
        <Spin aria-label={finishing ? 'Saving your choices' : 'Loading words'} />
      </Flex>
    );
  }

  if (summary) {
    const parts = [
      `${summary.learned} to learn`,
      `${summary.known} already known`,
      `${summary.later} for later`,
    ];
    return (
      <Result
        icon={<Echo mood="celebrate" size={150} style={{ margin: '0 auto' }} />}
        title="Nice work!"
        subTitle={`${parts.join(', ')}.${summary.later > 0 ? ' Words you chose "Not now" for come back at the end of the deck.' : ''}`}
        extra={[
          remaining > 0 && (
            <Button key="more" onClick={() => void load()}>
              Go through the {remaining} remaining word{remaining === 1 ? '' : 's'}
            </Button>
          ),
          <Button key="done" type="primary" onClick={onExit}>
            {exitLabel}
          </Button>,
        ]}
      />
    );
  }

  if (words.length === 0) {
    return (
      <Result
        icon={<Echo mood="celebrate" size={150} style={{ margin: '0 auto' }} />}
        title="You've been through every word in this deck"
        extra={
          <Button type="primary" onClick={onExit}>
            {exitLabel}
          </Button>
        }
      />
    );
  }

  return (
    <DeckSwipe
      key={round}
      title={title}
      items={words}
      renderFace={(word) => <DeckWordFace word={word} translationLanguage={language} />}
      borderColor={(word) => deckWordPalette(word).border}
      onDecision={handleDecision}
      onUndo={handleUndo}
      onDone={handleDone}
      onStop={stopLabel ? onExit : undefined}
      stopLabel={stopLabel}
    />
  );
};
